// src/components/admin/AdminGrantFreeAccess.jsx
import React, { useEffect, useState } from "react";
import { supabase } from "../../lib/supabaseClient";

const PLAN_OPTIONS = [
  { value: "", label: "— Ne pas changer le plan —" },
  { value: "standard", label: "Standard" },
  { value: "premium", label: "Premium" },
  { value: "art_advisor", label: "Art Advisor" },
  { value: "art_advisor_premium", label: "Art Advisor Premium" },
  { value: "family_office", label: "Family Office / CGP" },
  { value: "fondation", label: "Fondation" },
];

const BENEFICIARY_TYPE_OPTIONS = [
  { value: "", label: "— Non précisé —" },
  { value: "advisor", label: "Art Advisor" },
  { value: "influencer", label: "Influenceur" },
  { value: "ambassador", label: "Ambassadeur" },
  { value: "journalist", label: "Journaliste" },
  { value: "partner", label: "Partenaire" },
  { value: "beta_tester", label: "Bêta-testeur" },
  { value: "strategic_prospect", label: "Prospect stratégique" },
  { value: "collector", label: "Collectionneur" },
  { value: "institution", label: "Institution" },
  { value: "other", label: "Autre" },
];

const BUCKET_LABELS = {
  "j-1": { label: "J-1", color: "text-red-700 bg-red-50" },
  "j-7": { label: "J-7", color: "text-orange-700 bg-orange-50" },
  "j-14": { label: "J-14", color: "text-amber-700 bg-amber-50" },
  "j-30": { label: "J-30", color: "text-yellow-700 bg-yellow-50" },
  expired: { label: "Expiré", color: "text-gray-500 bg-gray-100" },
  ok: { label: "Actif", color: "text-green-700 bg-green-50" },
  revoked: { label: "Révoqué", color: "text-red-700 bg-red-50" },
  replaced: { label: "Remplacé", color: "text-gray-500 bg-gray-100" },
};

const ALERT_TABS = [
  { key: "attention", label: "À relancer (≤14j)", match: (a) => ["j-1", "j-7", "j-14"].includes(a.expiry_bucket) },
  { key: "j-30", label: "Expire sous 30j", match: (a) => a.expiry_bucket === "j-30" },
  { key: "expired", label: "Expirés", match: (a) => a.expiry_bucket === "expired" },
  { key: "active", label: "Actifs (OK)", match: (a) => a.expiry_bucket === "ok" },
  { key: "revoked", label: "Révoqués", match: (a) => a.expiry_bucket === "revoked" || a.expiry_bucket === "replaced" },
  { key: "all", label: "Tous", match: () => true },
];

export default function AdminGrantFreeAccess() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [months, setMonths] = useState(6);
  const [note, setNote] = useState("");
  const [sendInvite, setSendInvite] = useState(false);
  const [plan, setPlan] = useState("art_advisor");
  const [beneficiaryType, setBeneficiaryType] = useState("advisor");
  const [campaignCode, setCampaignCode] = useState("");
  const [reason, setReason] = useState("");
  const [startsAt, setStartsAt] = useState(""); // vide = démarre maintenant

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const [gifts, setGifts] = useState([]);
  const [loadingGifts, setLoadingGifts] = useState(true);
  const [revokingId, setRevokingId] = useState(null);
  const [extendingId, setExtendingId] = useState(null);
  const [notifyKey, setNotifyKey] = useState(null);
  const [grantersById, setGrantersById] = useState({});
  const [histStatus, setHistStatus] = useState("all"); // filtre statut historique
  const [histPlan, setHistPlan] = useState("all");      // filtre plan historique
  const [histCampaign, setHistCampaign] = useState("all"); // filtre campagne historique

  const [alerts, setAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(true);
  const [alertFilter, setAlertFilter] = useState("attention");

  const [campaignStats, setCampaignStats] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);

  // Récupérer l'historique des cadeaux
  async function fetchGifts() {
    setLoadingGifts(true);
    try {
      const { data: giftsData, error: giftsError } = await supabase
        .from("subscription_gifts")
        .select(
          "id, user_id, months, note, created_at, user_email, user_name, plan, status, starts_at, ends_at, beneficiary_type, campaign_code, reason, revoked_at, granted_by"
        )
        .order("created_at", { ascending: false })
        .limit(200);

      if (giftsError) {
        console.error("fetch gifts error", giftsError);
        setGifts([]);
        return;
      }

      setGifts(giftsData || []);

      // Résout le nom des admins ayant accordé (pour la colonne « Accordé par »).
      const granterIds = [...new Set((giftsData || []).map((g) => g.granted_by).filter(Boolean))];
      if (granterIds.length) {
        const { data: granters } = await supabase
          .from("profiles").select("id, first_name, last_name").in("id", granterIds);
        const map = {};
        (granters || []).forEach((p) => {
          map[p.id] = [p.first_name, p.last_name].filter(Boolean).join(" ") || null;
        });
        setGrantersById(map);
      }
    } catch (err) {
      console.error("Error fetching gifts:", err);
      setGifts([]);
    } finally {
      setLoadingGifts(false);
    }
  }

  // Alertes d'échéance (vue admin-only v_subscription_gifts_expiry_alerts)
  async function fetchAlerts() {
    setLoadingAlerts(true);
    try {
      const { data, error: alertsError } = await supabase
        .from("v_subscription_gifts_expiry_alerts")
        .select("*")
        .order("ends_at", { ascending: true })
        .limit(500);

      if (alertsError) {
        console.error("fetch alerts error", alertsError);
        setAlerts([]);
        return;
      }
      setAlerts(data || []);
    } catch (err) {
      console.error("Error fetching alerts:", err);
      setAlerts([]);
    } finally {
      setLoadingAlerts(false);
    }
  }

  // Tableau de conversion par campagne (vue admin-only v_subscription_gifts_campaign_stats)
  async function fetchCampaignStats() {
    setLoadingStats(true);
    try {
      const { data, error: statsError } = await supabase
        .from("v_subscription_gifts_campaign_stats")
        .select("*")
        .order("dernier_cadeau_le", { ascending: false });

      if (statsError) {
        console.error("fetch campaign stats error", statsError);
        setCampaignStats([]);
        return;
      }
      setCampaignStats(data || []);
    } catch (err) {
      console.error("Error fetching campaign stats:", err);
      setCampaignStats([]);
    } finally {
      setLoadingStats(false);
    }
  }

  useEffect(() => {
    fetchGifts();
    fetchAlerts();
    fetchCampaignStats();
  }, []);

  async function handleGrant(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;

      if (!token) {
        setError("Non authentifié");
        setLoading(false);
        return;
      }

      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const functionUrl = `${supabaseUrl}/functions/v1/grant-free-influencer`;

      console.log("Calling function:", functionUrl);

      const res = await fetch(functionUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          email,
          name: name || null,
          months: Number(months),
          note: note || null,
          sendInvite,
          plan: plan || null,
          beneficiaryType: beneficiaryType || null,
          campaignCode: campaignCode || null,
          reason: reason || null,
          startsAt: startsAt || null,
        })
      });

      const json = await res.json();
      console.log("Function response:", json);

      if (!res.ok) {
        setError(json?.error || `Erreur ${res.status}: ${res.statusText}`);
      } else {
        setResult(json);
        setEmail("");
        setName("");
        setMonths(6);
        setNote("");
        setSendInvite(false);
        setCampaignCode("");
        setReason("");
        setStartsAt("");
        fetchGifts();
        fetchAlerts();
        fetchCampaignStats();
      }
    } catch (err) {
      console.error("Error:", err);
      setError(err.message || "Erreur réseau");
    } finally {
      setLoading(false);
    }
  }

  async function handleRevoke(giftId) {
    if (!window.confirm("Révoquer ce cadeau ? L'accès gratuit sera recalculé immédiatement.")) return;
    setRevokingId(giftId);
    try {
      const { data, error: rpcError } = await supabase.rpc("revoke_subscription_gift", {
        p_gift_id: giftId,
        p_reason: "Révoqué depuis le dashboard admin",
      });
      if (rpcError) throw rpcError;
      if (!data?.success) throw new Error(data?.error || "Échec de la révocation");
      fetchGifts();
      fetchAlerts();
      fetchCampaignStats();
    } catch (err) {
      console.error("Error revoking gift:", err);
      alert("Erreur lors de la révocation : " + (err.message || "inconnue"));
    } finally {
      setRevokingId(null);
    }
  }

  // Prolonger un cadeau : ré-attribue via la même fonction. grant_free_access
  // étend free_until depuis l'échéance existante (ou depuis maintenant si expiré),
  // garde le plan/rôle (plan renvoyé) et journalise une nouvelle ligne (historique).
  async function handleExtend(g) {
    const input = window.prompt(`Prolonger l'accès de ${g.user_email} de combien de mois ?`, "3");
    if (input === null) return;
    const m = parseInt(input, 10);
    if (isNaN(m) || m <= 0 || m > 60) {
      alert("Nombre de mois invalide (1 à 60).");
      return;
    }
    setExtendingId(g.id);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) { alert("Non authentifié"); return; }

      const functionUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/grant-free-influencer`;
      const res = await fetch(functionUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          email: g.user_email,
          name: g.user_name || null,
          months: m,
          note: "Prolongation depuis le dashboard admin",
          sendInvite: false,
          plan: g.plan || null,               // garde le plan/rôle courant
          beneficiaryType: g.beneficiary_type || null,
          campaignCode: g.campaign_code || null,
          reason: "extension",
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || `Erreur ${res.status}`);
      fetchGifts();
      fetchAlerts();
      fetchCampaignStats();
    } catch (err) {
      console.error("Error extending gift:", err);
      alert("Erreur lors de la prolongation : " + (err.message || "inconnue"));
    } finally {
      setExtendingId(null);
    }
  }

  // Raccourcir : nouvelle échéance = aujourd'hui + N mois (recalcule free_until).
  async function handleShorten(g) {
    const input = window.prompt(`Réduire l'accès de ${g.user_email} à combien de mois à partir d'aujourd'hui ?`, "1");
    if (input === null) return;
    const m = parseInt(input, 10);
    if (isNaN(m) || m <= 0 || m > 60) { alert("Nombre de mois invalide (1 à 60)."); return; }
    setExtendingId(g.id);
    try {
      const { data, error: rpcError } = await supabase.rpc("shorten_subscription_gift", { p_gift_id: g.id, p_months: m });
      if (rpcError) throw rpcError;
      if (!data?.success) throw new Error(data?.error || "Échec");
      fetchGifts(); fetchAlerts(); fetchCampaignStats();
    } catch (err) {
      alert("Erreur lors du raccourcissement : " + (err.message || "inconnue"));
    } finally {
      setExtendingId(null);
    }
  }

  // Renvoyer l'invitation (magic link) ou proposer l'abonnement (email /tarifs).
  async function handleNotify(g, mode) {
    const label = mode === "resend_invite" ? "renvoyer l'invitation" : "envoyer la proposition d'abonnement";
    if (!window.confirm(`Confirmer : ${label} à ${g.user_email} ?`)) return;
    setNotifyKey(`${g.id}:${mode}`);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/gift-notify`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: g.user_email, name: g.user_name || null, mode }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || `Erreur ${res.status}`);
      alert("Email envoyé ✓");
    } catch (err) {
      alert("Erreur : " + (err.message || "inconnue"));
    } finally {
      setNotifyKey(null);
    }
  }

  // Filtres historique (#2)
  const campaignOptions = [...new Set(gifts.map((g) => g.campaign_code).filter(Boolean))];
  const filteredGifts = gifts.filter((g) =>
    (histStatus === "all" || g.status === histStatus) &&
    (histPlan === "all" || g.plan === histPlan) &&
    (histCampaign === "all" || g.campaign_code === histCampaign)
  );

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* En-tête */}
      <div className="border-b pb-4">
        <h2 className="text-3xl font-light tracking-tight">
          🎁 Offrir un accès gratuit
        </h2>
        <p className="text-gray-500 mt-2">Accordez un accès premium à vos influenceurs</p>
      </div>

      {/* Formulaire */}
      <form onSubmit={handleGrant} className="space-y-6 bg-white border rounded-lg p-6 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email *
            </label>
            <input
              type="email"
              placeholder="laetitia@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-black focus:border-transparent transition"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom (optionnel)
            </label>
            <input
              type="text"
              placeholder="Laetitia"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-black focus:border-transparent transition"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Durée (mois)
            </label>
            <input
              type="number"
              min="1"
              max="60"
              value={months}
              onChange={(e) => setMonths(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-black focus:border-transparent transition"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de début (optionnel)
            </label>
            <input
              type="date"
              value={startsAt}
              min={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setStartsAt(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-black focus:border-transparent transition"
            />
            <p className="text-xs text-gray-400 mt-1">
              Vide = démarre maintenant. Une date future = cadeau programmé (activé automatiquement le jour venu).
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Plan offert
            </label>
            <select
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-black focus:border-transparent transition"
            >
              {PLAN_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type de bénéficiaire
            </label>
            <select
              value={beneficiaryType}
              onChange={(e) => setBeneficiaryType(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-black focus:border-transparent transition"
            >
              {BENEFICIARY_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Campagne (optionnel)
            </label>
            <input
              type="text"
              placeholder="lancement-art-advisors-2026"
              value={campaignCode}
              onChange={(e) => setCampaignCode(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-black focus:border-transparent transition"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motif (optionnel)
            </label>
            <input
              type="text"
              placeholder="Invitation Art Advisor pilote"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-black focus:border-transparent transition"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Note (ex: campagne Potes)
            </label>
            <input
              type="text"
              placeholder="campagne Potes"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-4 py-2.5 focus:ring-2 focus:ring-black focus:border-transparent transition"
            />
          </div>

          <div className="md:col-span-2 flex items-center">
            <label className="inline-flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={sendInvite}
                onChange={(e) => setSendInvite(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-black focus:ring-black"
              />
              <span className="text-sm text-gray-700">Envoyer invitation email</span>
            </label>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t">
          <button
            type="submit"
            className="px-8 py-3 bg-black text-white rounded-md hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            disabled={loading}
          >
            {loading ? "Traitement…" : "Offrir l'accès"}
          </button>
        </div>
      </form>

      {/* Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-700">
          <strong>Erreur :</strong> {error}
        </div>
      )}

      {result && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-md">
          <div className="font-medium text-green-900 mb-2">✓ Succès</div>
          <div className="text-sm text-green-700 space-y-1">
            <div>Utilisateur : <strong>{result.user?.email}</strong></div>
            {result.user?.name && <div>Nom : {result.user.name}</div>}
            <div>
              Accès gratuit jusqu'au :{" "}
              {result.free_until
                ? new Date(result.free_until).toLocaleDateString("fr-FR", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })
                : "—"}
            </div>
            {result.created && (
              <div className="text-xs mt-2 text-green-600">
                ℹ️ Nouvel utilisateur créé
              </div>
            )}
          </div>
        </div>
      )}

      {/* Historique */}
      <div className="border-t pt-8">
        <h3 className="text-xl font-light mb-4">Historique des cadeaux</h3>

        {loadingGifts ? (
          <div className="text-gray-500 text-center py-8">Chargement…</div>
        ) : gifts.length === 0 ? (
          <div className="text-gray-500 text-center py-8 border rounded-md bg-gray-50">
            Aucun cadeau octroyé encore.
          </div>
        ) : (
          <>
          {/* Filtres (#2) */}
          <div className="flex flex-wrap gap-3 mb-4">
            <select value={histStatus} onChange={(e) => setHistStatus(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-3 py-1.5">
              <option value="all">Tous les statuts</option>
              <option value="active">Actifs</option>
              <option value="scheduled">Programmés</option>
              <option value="expired">Expirés</option>
              <option value="revoked">Révoqués</option>
              <option value="replaced">Remplacés</option>
            </select>
            <select value={histPlan} onChange={(e) => setHistPlan(e.target.value)}
              className="text-sm border border-gray-300 rounded-md px-3 py-1.5">
              <option value="all">Tous les plans</option>
              {PLAN_OPTIONS.filter((o) => o.value).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {campaignOptions.length > 0 && (
              <select value={histCampaign} onChange={(e) => setHistCampaign(e.target.value)}
                className="text-sm border border-gray-300 rounded-md px-3 py-1.5">
                <option value="all">Toutes les campagnes</option>
                {campaignOptions.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            <span className="text-sm text-gray-400 self-center">{filteredGifts.length} / {gifts.length}</span>
          </div>

          {filteredGifts.length === 0 ? (
            <div className="text-gray-500 text-center py-8 border rounded-md bg-gray-50">
              Aucun cadeau pour ces filtres.
            </div>
          ) : (
          <div className="space-y-2">
            {filteredGifts.map((g) => {
              const displayName = g.user_name || g.user_email || g.user_id;
              const hasNameAndEmail = g.user_name && g.user_email;
              const statusLabel = {
                active: "Actif",
                scheduled: "Programmé",
                expired: "Expiré",
                revoked: "Révoqué",
                replaced: "Remplacé",
              }[g.status] || g.status;
              const statusColor = {
                active: "text-green-700 bg-green-50",
                scheduled: "text-blue-700 bg-blue-50",
                expired: "text-gray-500 bg-gray-100",
                revoked: "text-red-700 bg-red-50",
                replaced: "text-gray-500 bg-gray-100",
              }[g.status] || "text-gray-500 bg-gray-100";
              const beneficiaryLabel = BENEFICIARY_TYPE_OPTIONS.find((o) => o.value === g.beneficiary_type)?.label;
              const planLabel = PLAN_OPTIONS.find((o) => o.value === g.plan)?.label;

              return (
                <div
                  key={g.id}
                  className="border rounded-md p-4 hover:bg-gray-50 transition flex justify-between items-center gap-4"
                >
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 flex items-center gap-2">
                      {displayName}
                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor}`}>{statusLabel}</span>
                      {planLabel && <span className="text-xs px-2 py-0.5 rounded-full text-gray-700 bg-gray-100">{planLabel}</span>}
                      {beneficiaryLabel && <span className="text-xs px-2 py-0.5 rounded-full text-gray-700 bg-gray-100">{beneficiaryLabel}</span>}
                    </div>
                    {hasNameAndEmail && (
                      <div className="text-sm text-gray-600">{g.user_email}</div>
                    )}
                    <div className="text-xs text-gray-500 mt-1">
                      {g.reason || g.note || g.campaign_code || "—"} · accordé le {new Date(g.created_at).toLocaleDateString("fr-FR", {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                      {g.ends_at && (
                        <> · jusqu'au {new Date(g.ends_at).toLocaleDateString("fr-FR", { year: 'numeric', month: 'long', day: 'numeric' })}</>
                      )}
                      {grantersById[g.granted_by] && (
                        <> · par {grantersById[g.granted_by]}</>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-medium text-gray-900">{g.months}</div>
                    <div className="text-xs text-gray-500">mois</div>
                  </div>
                  {g.status !== "revoked" && (
                    <div className="flex flex-col gap-2 shrink-0 w-40">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleExtend(g)}
                          disabled={extendingId === g.id}
                          className="flex-1 text-xs px-2 py-1.5 border border-gray-300 text-gray-800 rounded-md hover:bg-gray-100 transition disabled:opacity-50"
                        >
                          {extendingId === g.id ? "…" : "Prolonger"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleShorten(g)}
                          disabled={extendingId === g.id}
                          className="flex-1 text-xs px-2 py-1.5 border border-gray-300 text-gray-800 rounded-md hover:bg-gray-100 transition disabled:opacity-50"
                        >
                          Raccourcir
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRevoke(g.id)}
                        disabled={revokingId === g.id}
                        className="text-xs px-2 py-1.5 border border-red-200 text-red-700 rounded-md hover:bg-red-50 transition disabled:opacity-50"
                      >
                        {revokingId === g.id ? "…" : "Révoquer"}
                      </button>
                      <div className="flex gap-3 justify-center pt-1">
                        <button
                          type="button"
                          onClick={() => handleNotify(g, "resend_invite")}
                          disabled={notifyKey === `${g.id}:resend_invite`}
                          className="text-[11px] text-gray-500 underline hover:text-gray-800 disabled:opacity-50"
                        >
                          {notifyKey === `${g.id}:resend_invite` ? "…" : "Renvoyer"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleNotify(g, "convert_offer")}
                          disabled={notifyKey === `${g.id}:convert_offer`}
                          className="text-[11px] text-gray-500 underline hover:text-gray-800 disabled:opacity-50"
                        >
                          {notifyKey === `${g.id}:convert_offer` ? "…" : "Proposer l'abo"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          )}
          </>
        )}
      </div>

      {/* Alertes d'échéance */}
      <div className="border-t pt-8">
        <div className="flex items-baseline justify-between mb-1">
          <h3 className="text-xl font-light">Alertes d'échéance</h3>
          <span className="text-xs text-gray-400">Campagne septembre — {alerts.length} cadeau(x)</span>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Suivi des accès offerts qui approchent de leur date de fin, pour relancer avant expiration.
        </p>

        <div className="flex flex-wrap gap-2 mb-4">
          {ALERT_TABS.map((tab) => {
            const count = alerts.filter((a) => tab.match(a)).length;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setAlertFilter(tab.key)}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${
                  alertFilter === tab.key
                    ? "bg-black text-white border-black"
                    : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                }`}
              >
                {tab.label} {count > 0 && <span className="opacity-60">({count})</span>}
              </button>
            );
          })}
        </div>

        {loadingAlerts ? (
          <div className="text-gray-500 text-center py-8">Chargement…</div>
        ) : (
          (() => {
            const activeTab = ALERT_TABS.find((t) => t.key === alertFilter) || ALERT_TABS[0];
            const filtered = alerts.filter((a) => activeTab.match(a));
            if (filtered.length === 0) {
              return (
                <div className="text-gray-500 text-center py-8 border rounded-md bg-gray-50">
                  Aucun cadeau dans cette catégorie.
                </div>
              );
            }
            return (
              <div className="space-y-2">
                {filtered.map((a) => {
                  const bucket = BUCKET_LABELS[a.expiry_bucket] || { label: a.expiry_bucket, color: "text-gray-500 bg-gray-100" };
                  const displayName = a.user_name || a.user_email || a.user_id;
                  return (
                    <div
                      key={a.gift_id}
                      className="border rounded-md p-4 hover:bg-gray-50 transition flex justify-between items-center gap-4"
                    >
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 flex items-center gap-2">
                          {displayName}
                          <span className={`text-xs px-2 py-0.5 rounded-full ${bucket.color}`}>{bucket.label}</span>
                          {a.has_paid_subscription && (
                            <span className="text-xs px-2 py-0.5 rounded-full text-emerald-700 bg-emerald-50">Converti (abonné payant)</span>
                          )}
                          {!a.has_logged_in && (
                            <span className="text-xs px-2 py-0.5 rounded-full text-amber-700 bg-amber-50">Jamais connecté</span>
                          )}
                        </div>
                        <div className="text-sm text-gray-600">{a.user_email}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {a.campaign_code || "(sans campagne)"} · {a.plan || "—"}
                          {a.ends_at && (
                            <> · fin le {new Date(a.ends_at).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" })}</>
                          )}
                        </div>
                      </div>
                      <div className="text-right text-xs text-gray-500">
                        {a.days_remaining != null && a.days_remaining >= 0 ? `J-${a.days_remaining}` : ""}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()
        )}
      </div>

      {/* Tableau de conversion par campagne */}
      <div className="border-t pt-8">
        <h3 className="text-xl font-light mb-1">Conversion par campagne</h3>
        <p className="text-sm text-gray-500 mb-4">
          Taux d'activation (connexion) et de conversion (passage à un abonnement payant) des invitations offertes.
        </p>

        {loadingStats ? (
          <div className="text-gray-500 text-center py-8">Chargement…</div>
        ) : campaignStats.length === 0 ? (
          <div className="text-gray-500 text-center py-8 border rounded-md bg-gray-50">
            Aucune donnée de campagne encore.
          </div>
        ) : (
          <div className="overflow-x-auto border rounded-md">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-2.5">Campagne</th>
                  <th className="text-left px-4 py-2.5">Type</th>
                  <th className="text-right px-4 py-2.5">Cadeaux</th>
                  <th className="text-right px-4 py-2.5">Actifs</th>
                  <th className="text-right px-4 py-2.5">Expirés</th>
                  <th className="text-right px-4 py-2.5">Révoqués</th>
                  <th className="text-right px-4 py-2.5">Activés</th>
                  <th className="text-right px-4 py-2.5">Taux activation</th>
                  <th className="text-right px-4 py-2.5">Convertis</th>
                  <th className="text-right px-4 py-2.5">Taux conversion</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {campaignStats.map((s, i) => (
                  <tr key={`${s.campaign_code}-${s.beneficiary_type}-${i}`} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 font-medium text-gray-900">{s.campaign_code || "(sans campagne)"}</td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {BENEFICIARY_TYPE_OPTIONS.find((o) => o.value === s.beneficiary_type)?.label || s.beneficiary_type || "—"}
                    </td>
                    <td className="px-4 py-2.5 text-right">{s.nb_cadeaux}</td>
                    <td className="px-4 py-2.5 text-right">{s.nb_actifs}</td>
                    <td className="px-4 py-2.5 text-right">{s.nb_expires}</td>
                    <td className="px-4 py-2.5 text-right">{s.nb_revoques}</td>
                    <td className="px-4 py-2.5 text-right">{s.nb_ont_ete_actives}</td>
                    <td className="px-4 py-2.5 text-right">{s.taux_activation_pct != null ? `${s.taux_activation_pct}%` : "—"}</td>
                    <td className="px-4 py-2.5 text-right font-medium">{s.nb_convertis}</td>
                    <td className="px-4 py-2.5 text-right font-medium">
                      {s.taux_conversion_pct != null ? `${s.taux_conversion_pct}%` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}